import { useState, useEffect } from 'react'
import { useWalletClient, useSwitchChain, useChainId } from 'wagmi'
import {
  FiX,
  FiCheck,
  FiAlertCircle,
  FiCreditCard,
  FiTrendingUp,
  FiZap,
  FiRefreshCw,
} from 'react-icons/fi'
import { ethers } from 'ethers'
import walletService from '../services/walletService'
import { getEthersSignerFromWalletClient } from '../services/wallet'
import { confirmPayment } from '../services/api'
import { getPolygonscanUrl } from '../utils/formatters'

const LAB_WALLET =
  import.meta.env.VITE_M1NT_LAB_PAYMENT_WALLET || '0x26F30C00A68e5d87072b3127D97Fa1317501AcBe'

const SUPPORTED_NETWORKS = [
  { chainId: 1, name: 'Ethereum', icon: '💎', color: 'from-blue-500 to-indigo-500' },
  { chainId: 137, name: 'Polygon', icon: '🟣', color: 'from-purple-500 to-violet-500' },
  { chainId: 42161, name: 'Arbitrum', icon: '🔵', color: 'from-blue-400 to-cyan-500' },
  { chainId: 10, name: 'Optimism', icon: '🔴', color: 'from-red-500 to-pink-500' },
  { chainId: 8453, name: 'Base', icon: '🔷', color: 'from-blue-600 to-blue-400' },
  { chainId: 56, name: 'BNB Chain', icon: '💛', color: 'from-yellow-500 to-amber-500' },
  { chainId: 80002, name: 'Polygon Amoy (Test)', icon: '🟣', color: 'from-purple-400 to-pink-400' },
]

export default function PaymentModal({ order, isOpen, onClose, onSuccess }) {
  const { data: walletClient } = useWalletClient()
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain()
  const currentChainId = useChainId()
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [paymentOptions, setPaymentOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [userAddress, setUserAddress] = useState(null)
  const [network, setNetwork] = useState(null)
  const [recommendedSymbol, setRecommendedSymbol] = useState(null)
  const [error, setError] = useState(null)
  const [showNetworkSelector, setShowNetworkSelector] = useState(false)

  useEffect(() => {
    if (isOpen && order) {
      console.log('🔄 Initializing payment, chainId:', currentChainId)
      initializePayment()
    } else {
      setPaymentOptions([])
      setSelectedOption(null)
      setUserAddress(null)
      setNetwork(null)
      setRecommendedSymbol(null)
      setError(null)
      setShowNetworkSelector(false)
    }
  }, [isOpen, order, walletClient, currentChainId])

  const handleSwitchNetwork = async (chainId) => {
    console.log('🔄 Switching to chain:', chainId)
    try {
      setError(null)
      setLoading(true)
      await switchChainAsync({ chainId })
      console.log('✅ Network switched successfully')
      setShowNetworkSelector(false)
    } catch (err) {
      console.error('Failed to switch network:', err)
      if (err?.code === 4001 || err?.message?.includes('rejected')) {
        setError('Network switch cancelled.')
      } else {
        setError('Failed to switch network. Please try manually in your wallet.')
      }
      setLoading(false)
    }
  }

  const formatAmountForUnits = (amountNumber, decimals) => {
    const max = Math.min(decimals ?? 18, 18)
    // Avoid scientific notation; keep enough precision for tiny payments.
    return Number(amountNumber)
      .toFixed(max)
      .replace(/\.?0+$/, '')
  }

  const initializePayment = async () => {
    const hasPrice = order?.priceUSD != null || order?.price != null
    if (!hasPrice) {
      setPaymentOptions([])
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const signer = walletClient ? await getEthersSignerFromWalletClient(walletClient) : null
      await walletService.initialize(signer ?? undefined)
      const net = walletService.network
      setNetwork(net)
      const address = await walletService.signer.getAddress()
      setUserAddress(address)

      let usdAmount
      if (order.priceUSD != null) {
        usdAmount = parseFloat(order.priceUSD)
      } else {
        const prices = await walletService.getCryptoPrices()
        usdAmount = parseFloat(order.price) * (prices.MATIC ?? 0.8)
      }

      const options = await walletService.getPaymentOptionsWithBalances(usdAmount, address)
      setPaymentOptions(options)

      const best = options
        .filter((opt) => opt.hasEnough)
        .sort((a, b) => (b.balanceUSD ?? 0) - (a.balanceUSD ?? 0))[0]
      if (best) {
        setRecommendedSymbol(best.symbol)
        setSelectedOption(best)
      } else {
        setRecommendedSymbol(null)
      }
    } catch (err) {
      console.error('Initialization error:', err)
      setError(err.message || 'Failed to load payment options. Please try again.')
      setPaymentOptions([])
    } finally {
      setLoading(false)
    }
  }

  const handlePay = async () => {
    if (!selectedOption?.hasEnough) {
      setError(
        selectedOption
          ? `Insufficient ${selectedOption.displaySymbol || selectedOption.symbol} balance`
          : 'Please select a payment method'
      )
      return
    }

    setPaying(true)
    setError(null)

    try {
      // eslint-disable-next-line no-console
      console.log('💰 PAYMENT DETAILS:')
      // eslint-disable-next-line no-console
      console.log(
        '- Amount:',
        selectedOption.requiredAmount,
        selectedOption.displaySymbol || selectedOption.symbol
      )
      // eslint-disable-next-line no-console
      console.log('- To Address:', LAB_WALLET)
      // eslint-disable-next-line no-console
      console.log('- From Address:', userAddress)
      // eslint-disable-next-line no-console
      console.log('- Order ID:', order.id)
      // eslint-disable-next-line no-console
      console.log('- Price USD:', order.priceUSD)

      let txHash
      const amountStr = formatAmountForUnits(selectedOption.requiredAmount, selectedOption.decimals)

      if (selectedOption.isNative) {
        const tx = await walletService.signer.sendTransaction({
          to: LAB_WALLET,
          value: ethers.parseEther(amountStr),
        })
        await tx.wait()
        txHash = tx.hash
      } else {
        const tokenContract = new ethers.Contract(
          selectedOption.address,
          [
            'function transfer(address to, uint256 amount) returns (bool)',
            'function balanceOf(address account) view returns (uint256)',
          ],
          walletService.signer
        )
        const amountWei = ethers.parseUnits(amountStr, selectedOption.decimals)
        const tx = await tokenContract.transfer(LAB_WALLET, amountWei)
        await tx.wait()
        txHash = tx.hash
      }

      // eslint-disable-next-line no-console
      console.log('✅ TX HASH:', txHash)
      // eslint-disable-next-line no-console
      console.log('📊 View on PolygonScan:', getPolygonscanUrl(txHash))

      await confirmPayment(order.id, {
        txHash,
        amount: parseFloat(amountStr),
        from: userAddress,
        to: LAB_WALLET,
      })

      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Payment failed:', err)
      let errorMsg = 'Payment failed. '
      if (err?.code === 'ACTION_REJECTED') errorMsg += 'Transaction was rejected.'
      else if (err?.code === 'INSUFFICIENT_FUNDS') errorMsg += 'Insufficient funds for gas fees.'
      else errorMsg += err.message || 'Please try again.'
      setError(errorMsg)
    } finally {
      setPaying(false)
    }
  }

  const usdDisplay =
    order?.priceUSD != null
      ? parseFloat(order.priceUSD).toFixed(2)
      : order?.price != null
        ? (parseFloat(order.price) * (paymentOptions[0]?.rate ?? 0.8)).toFixed(2)
        : '—'

  const totalBalanceUsd = paymentOptions.reduce((sum, opt) => sum + (opt.balanceUSD || 0), 0)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-6 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-900 z-10">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <FiCreditCard className="w-7 h-7 text-cyan-400" />
              Complete Payment
            </h2>
            <p className="text-gray-400 text-sm mt-1">Smart payment detection enabled</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={paying}
            className="text-gray-400 hover:text-white transition disabled:opacity-50 p-2"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto" />
              <p className="text-gray-400 mt-4">Detecting wallet balances...</p>
            </div>
          ) : error && paymentOptions.length === 0 ? (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6 flex items-center gap-2 text-red-500">
              <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <>
              {userAddress && (
                <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Connected Wallet</div>
                      <div className="text-white font-mono text-sm">
                        {userAddress.slice(0, 6)}...{userAddress.slice(-4)}
                      </div>
                    </div>
                    {paymentOptions.length > 0 && (
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Balance</div>
                        <div className="text-cyan-400 font-medium">
                          ${totalBalanceUsd.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                  {network && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {SUPPORTED_NETWORKS.find((n) => n.chainId === network.chainId)?.icon ||
                            '🌐'}
                        </span>
                        <span className="text-sm text-white">
                          {SUPPORTED_NETWORKS.find((n) => n.chainId === network.chainId)?.name ||
                            network.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowNetworkSelector(!showNetworkSelector)}
                        disabled={paying || isSwitching}
                        className="text-xs px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition flex items-center gap-1 disabled:opacity-50"
                      >
                        <FiRefreshCw className={`w-3 h-3 ${isSwitching ? 'animate-spin' : ''}`} />
                        Switch Network
                      </button>
                    </div>
                  )}

                  {showNetworkSelector && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <p className="text-xs text-gray-400 mb-2">Select a network with funds:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {SUPPORTED_NETWORKS.filter((n) => n.chainId !== 80002).map((net) => (
                          <button
                            key={net.chainId}
                            type="button"
                            onClick={() => handleSwitchNetwork(net.chainId)}
                            disabled={isSwitching || network?.chainId === net.chainId}
                            className={`p-2 rounded-lg border text-left transition flex items-center gap-2 ${
                              network?.chainId === net.chainId
                                ? 'border-cyan-500 bg-cyan-500/20'
                                : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                            } disabled:opacity-50`}
                          >
                            <span className="text-xl">{net.icon}</span>
                            <span className="text-sm text-white">{net.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
                <h3 className="text-white font-medium mb-2">Order Details</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-400">
                    Card: <span className="text-white font-medium">{order.card?.name}</span>
                  </p>
                  <p className="text-gray-400">
                    Total Price:{' '}
                    <span className="text-cyan-400 font-bold text-3xl">${usdDisplay}</span>{' '}
                    <span className="text-gray-500 text-base">USD</span>
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <FiTrendingUp className="w-5 h-5 text-cyan-400" />
                  Select Payment Method
                </h3>

                {paymentOptions.map((option) => (
                  <button
                    key={option.symbol}
                    type="button"
                    onClick={() => option.hasEnough && setSelectedOption(option)}
                    disabled={!option.hasEnough || paying}
                    className={`w-full p-4 rounded-lg border-2 transition text-left relative ${
                      selectedOption?.symbol === option.symbol
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : option.hasEnough
                          ? 'border-gray-700 hover:border-gray-600'
                          : 'border-gray-800 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {option.hasEnough && recommendedSymbol === option.symbol && (
                      <div className="absolute top-2 right-2 border border-m1-ice bg-m1-surface text-m1-ice text-xs font-bold px-2 py-1 flex items-center gap-1">
                        <FiZap className="w-3 h-3" />✅ Recommended
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{option.icon}</span>
                        <div>
                          <div className="text-white font-medium">{option.name}</div>
                          <div className="text-gray-400 text-sm">
                            {option.requiredAmountFormatted}
                          </div>
                          <div className="text-xs mt-1">
                            {option.hasEnough ? (
                              <span className="text-green-400">
                                ✓ Balance:{' '}
                                {option.balance.toFixed(option.symbol === 'MATIC' ? 4 : 2)}{' '}
                                {option.displaySymbol || option.symbol}
                              </span>
                            ) : (
                              <span className="text-red-400">
                                ✗ Insufficient (have:{' '}
                                {option.balance.toFixed(option.symbol === 'MATIC' ? 4 : 2)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {selectedOption?.symbol === option.symbol && (
                        <FiCheck className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {!paymentOptions.some((opt) => opt.hasEnough) && paymentOptions.length > 0 && (
                <div className="bg-orange-500/10 border border-orange-500 rounded-lg p-4 mb-6">
                  <p className="text-orange-400 text-sm flex items-center gap-2 mb-2">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                    No sufficient balance on this network.
                  </p>
                  <p className="text-gray-400 text-xs">
                    Try switching to a network where you have funds (Ethereum, Polygon, etc.) using
                    the &quot;Switch Network&quot; button above.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6 flex items-center gap-2 text-red-500">
                  <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-6">
                <p className="text-yellow-500 text-sm flex items-center gap-2">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  Make sure you have enough{' '}
                  {selectedOption?.displaySymbol || selectedOption?.symbol || 'tokens'} in your
                  wallet before proceeding
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={!selectedOption?.hasEnough || paying}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {paying ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiCreditCard className="w-5 h-5" />
                      Pay {selectedOption?.requiredAmountFormatted || ''}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={paying}
                  className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
